// The 2-seat board-game table set (chess, checkers): a square charcoal table
// with a brushed-gold band, a burgundy felt top with an inlaid 8×8 board, and
// two chairs facing each other — the same palette as the card-table set
// (felt burgundy #5a1424, charcoal #1d2226, brushed gold #b8862e).
//
// Procedural and light for Quest. Each part is its own component so a Blender
// GLB can replace it later: swap `BoardTable` / `BoardChair` for a GLB
// wrapper like CardTableModel.tsx (Suspense + error boundary, primitive
// fallback) keeping the same frame — floor origin, the board centred at
// (0, BOARD_Y, 0) with a1 toward +Z/−X, chairs at BOARD_CHAIRS.

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { BOARD_CHAIRS, BOARD_W, BT_TOP, BT_W, SEAT_Y } from './boardLayout';
import type { Chair } from '../types';

export const FELT = '#5a1424';
export const FRAME = '#1d2226';
export const GOLD = '#b8862e';
export const LIGHT_SQ = '#e6d3a8';
export const DARK_SQ = '#6e1e2c';

const deco = { collidable: false };

let boardTex: THREE.CanvasTexture | null = null;
/** The inlaid board: 8×8 squares inside a thin gold line (one shared texture). */
function boardTexture(): THREE.Texture | null {
  if (boardTex) return boardTex;
  if (typeof document === 'undefined') return null;
  const N = 512, pad = 14, s = (N - pad * 2) / 8;
  const c = document.createElement('canvas');
  c.width = c.height = N;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = FRAME; g.fillRect(0, 0, N, N);
  g.fillStyle = GOLD; g.fillRect(4, 4, N - 8, N - 8);
  g.fillStyle = FRAME; g.fillRect(9, 9, N - 18, N - 18);
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      // Canvas top = rank 8 (−Z); a1 (file 0, rank 0) is dark.
      g.fillStyle = (f + r) % 2 === 0 ? DARK_SQ : LIGHT_SQ;
      g.fillRect(pad + f * s, pad + (7 - r) * s, s, s);
    }
  }
  boardTex = new THREE.CanvasTexture(c);
  boardTex.colorSpace = THREE.SRGBColorSpace;
  boardTex.anisotropy = 4;
  return boardTex;
}

/** Table with the inlaid board. Top surface at BT_TOP. */
export function BoardTable() {
  const tex = useMemo(boardTexture, []);
  const leg = BT_W / 2 - 0.07;
  const inset = BOARD_W + (BOARD_W * 28) / 484; // board + its frame (texture padding)
  return (
    <group userData={deco}>
      {/* Charcoal top slab + gold band under its lip */}
      <mesh position={[0, BT_TOP - 0.02, 0]} castShadow receiveShadow userData={deco}>
        <boxGeometry args={[BT_W, 0.04, BT_W]} />
        <meshStandardMaterial color={FRAME} roughness={0.5} />
      </mesh>
      <mesh position={[0, BT_TOP - 0.047, 0]} userData={deco}>
        <boxGeometry args={[BT_W + 0.012, 0.014, BT_W + 0.012]} />
        <meshStandardMaterial color={GOLD} roughness={0.35} metalness={0.7} />
      </mesh>
      {/* Felt top */}
      <mesh position={[0, BT_TOP + 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow userData={deco}>
        <planeGeometry args={[BT_W - 0.05, BT_W - 0.05]} />
        <meshStandardMaterial color={FELT} roughness={0.95} />
      </mesh>
      {/* Inlaid board */}
      <mesh position={[0, BT_TOP + 0.003, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow userData={deco}>
        <planeGeometry args={[inset, inset]} />
        <meshStandardMaterial map={tex ?? undefined} color={tex ? '#ffffff' : LIGHT_SQ} roughness={0.55} />
      </mesh>
      {/* Legs with gold feet */}
      {[[-leg, -leg], [leg, -leg], [-leg, leg], [leg, leg]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, (BT_TOP - 0.05) / 2 + 0.02, 0]} castShadow userData={deco}>
            <cylinderGeometry args={[0.022, 0.016, BT_TOP - 0.09, 10]} />
            <meshStandardMaterial color={FRAME} roughness={0.5} />
          </mesh>
          <mesh position={[0, 0.012, 0]} userData={deco}>
            <cylinderGeometry args={[0.02, 0.024, 0.024, 10]} />
            <meshStandardMaterial color={GOLD} roughness={0.35} metalness={0.7} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/** One chair: the sitter faces local +Z (rotate by the chair's yaw). Cushion top at SEAT_Y. */
export function BoardChair({ chair }: { chair: Chair }) {
  const legY = (SEAT_Y - 0.06) / 2;
  return (
    <group position={[chair.x, 0, chair.z]} rotation={[0, chair.yaw, 0]} userData={deco}>
      <mesh position={[0, SEAT_Y - 0.03, 0]} castShadow userData={deco}>
        <boxGeometry args={[0.44, 0.06, 0.44]} />
        <meshStandardMaterial color={FELT} roughness={0.8} />
      </mesh>
      <mesh position={[0, SEAT_Y - 0.07, 0]} userData={deco}>
        <boxGeometry args={[0.45, 0.025, 0.45]} />
        <meshStandardMaterial color={FRAME} roughness={0.5} />
      </mesh>
      {[[-0.19, -0.19], [0.19, -0.19], [-0.19, 0.19], [0.19, 0.19]].map(([x, z], k) => (
        <mesh key={k} position={[x, legY, z]} userData={deco}>
          <cylinderGeometry args={[0.016, 0.013, SEAT_Y - 0.06, 8]} />
          <meshStandardMaterial color={FRAME} roughness={0.5} />
        </mesh>
      ))}
      {/* Back rest + gold rail */}
      <mesh position={[0, SEAT_Y + 0.25, -0.205]} castShadow userData={deco}>
        <boxGeometry args={[0.44, 0.44, 0.035]} />
        <meshStandardMaterial color={FRAME} roughness={0.5} />
      </mesh>
      <mesh position={[0, SEAT_Y + 0.25, -0.185]} userData={deco}>
        <boxGeometry args={[0.32, 0.3, 0.008]} />
        <meshStandardMaterial color={FELT} roughness={0.8} />
      </mesh>
      <mesh position={[0, SEAT_Y + 0.48, -0.205]} rotation={[0, 0, Math.PI / 2]} userData={deco}>
        <cylinderGeometry args={[0.014, 0.014, 0.46, 10]} />
        <meshStandardMaterial color={GOLD} roughness={0.35} metalness={0.7} />
      </mesh>
    </group>
  );
}

/** Furniture for chess / checkers: table + two chairs, standing on the floor. */
export function BoardTableSet() {
  return (
    <>
      <BoardTable />
      {BOARD_CHAIRS.map((ch, i) => <BoardChair key={i} chair={ch} />)}
    </>
  );
}

export default BoardTableSet;
